import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FolderKanban, TrendingUp, DollarSign } from "lucide-react";
import { exportToCSV } from "./ReportExport";

export default function ProjectOverviewReport({ projects, timeEntries, invoices = [], startDate, endDate }) {
  // Filter to projects that have time entries within the selected period
  const periodProjects = projects.filter(project => {
    return timeEntries.some(e => {
      if (e.project_id !== project.id) return false;
      const entryDate = new Date(e.start_time || e.created_date);
      return entryDate >= startDate && entryDate <= endDate;
    });
  });

  const projectData = periodProjects.map(project => {
    // Only count time entries within the period for hours/cost
    const projectTimeEntries = timeEntries.filter(e => {
      if (e.project_id !== project.id) return false;
      const entryDate = new Date(e.start_time || e.created_date);
      return entryDate >= startDate && entryDate <= endDate;
    });
    const totalHours = projectTimeEntries.reduce((sum, e) => sum + (e.duration_minutes || 0) / 60, 0);
    const totalCost = totalHours * (project.hourly_rate || 2500);
    const projectInvoices = invoices.filter(inv => inv.project_id === project.id);
    const invoiced = projectInvoices.reduce((sum, inv) => sum + inv.total_amount, 0);
    const paid = projectInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total_amount, 0);
    
    return {
      name: project.name,
      status: project.status,
      owner: project.owner_name || project.owner,
      budget: project.budget || 0,
      actualCost: totalCost,
      totalHours: totalHours.toFixed(1),
      budgetRemaining: (project.budget || 0) - totalCost,
      invoiced: invoiced,
      paid: paid,
      toBeBilled: totalCost - invoiced,
      startDate: project.start_date || "Ikke satt",
      endDate: project.end_date || "Ikke satt",
    };
  });

  const stats = {
    total: projectData.length,
    active: projectData.filter(p => p.status === "active").length,
    completed: projectData.filter(p => p.status === "completed").length,
    totalBudget: projectData.reduce((sum, p) => sum + (p.budget || 0), 0),
    totalSpent: projectData.reduce((sum, p) => sum + p.actualCost, 0),
    totalInvoiced: projectData.reduce((sum, p) => sum + p.invoiced, 0),
    totalPaid: projectData.reduce((sum, p) => sum + p.paid, 0),
  };

  const handleExport = () => {
    const exportData = projectData.map(p => ({
      "Prosjektnavn": p.name,
      "Status": p.status,
      "Eier": p.owner,
      "Budsjett (kr)": p.budget,
      "Faktisk kostnad (kr)": p.actualCost.toFixed(0),
      "Timer brukt": p.totalHours,
      "Budsjett gjenstående (kr)": p.budgetRemaining.toFixed(0),
      "Startdato": p.startDate,
      "Sluttdato": p.endDate,
    }));
    exportToCSV(exportData, `prosjektoversikt_${new Date().toISOString().split('T')[0]}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Prosjektoversikt</h3>
        <Button onClick={handleExport} variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          Eksporter til CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <FolderKanban className="w-4 h-4 text-zinc-900" />
            <p className="text-xs text-slate-500">Prosjekter</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            <p className="text-xs text-slate-500">Aktive</p>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{stats.active}</p>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-zinc-900" />
            <p className="text-xs text-slate-500">Budsjett</p>
          </div>
          <p className="text-lg font-bold text-slate-900">
            {(stats.totalBudget / 1000000).toFixed(1)}M
          </p>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-amber-600" />
            <p className="text-xs text-slate-500">Brukt</p>
          </div>
          <p className="text-lg font-bold text-amber-600">
            {(stats.totalSpent / 1000000).toFixed(1)}M
          </p>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-blue-600" />
            <p className="text-xs text-slate-500">Fakturert</p>
          </div>
          <p className="text-lg font-bold text-blue-600">
            {(stats.totalInvoiced / 1000000).toFixed(1)}M
          </p>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-green-600" />
            <p className="text-xs text-slate-500">Betalt</p>
          </div>
          <p className="text-lg font-bold text-green-600">
            {(stats.totalPaid / 1000000).toFixed(1)}M
          </p>
        </Card>
      </div>

      <Card className="bg-white border-0 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Prosjekt</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Timer</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Brukt</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Fakturert</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Betalt</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600">Ufakturert</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {projectData.map((project, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{project.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{project.status}</td>
                  <td className="px-4 py-3 text-sm text-slate-900 text-right">{project.totalHours}h</td>
                  <td className="px-4 py-3 text-sm text-amber-600 text-right">
                    {project.actualCost.toLocaleString()} kr
                  </td>
                  <td className="px-4 py-3 text-sm text-blue-600 text-right">
                    {project.invoiced.toLocaleString()} kr
                  </td>
                  <td className="px-4 py-3 text-sm text-green-600 text-right">
                    {project.paid.toLocaleString()} kr
                  </td>
                  <td className="px-4 py-3 text-sm text-orange-600 text-right">
                    {project.toBeBilled.toLocaleString()} kr
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}